import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import Select from 'react-select';

import { Formik, Form } from 'formik';
import Loader from '@components/Loader';
import Layout from '@containers/Layout';
import Button, { SIZE, VARIANT } from '@components/Button';
import { Box, Label } from '@styles/globalStyles';
import {
  Col,
  Row,
  Title,
  Detail,
  Description,
  ScrollableList,
  DownloadBar,
  Buttons,
  TracksDownloadWrapper,
  AlbumDownloadWrapper,
} from '@containers/AlbumPage/style';
import TracksList from '@components/Tracks';
import ListHeader from '@components/ListHeader';
import {
  albumOptions,
  tracksOptions,
  stemsOptions,
  stemAlbumOptions,
} from '@app/consts';

const selectStyles = {
  container: provided => ({
    ...provided,
    width: '100%',
    maxWidth: '300px',
  }),
};

const AlbumPage = inject('musicStore')(
  observer(
    ({
      musicStore: {
        fetchAlbum,
        isLoading,
        album,
        resetAlbum,
        downloadAlbum,
        downloadTracks,
      },
    }) => {
      const { uuid, kind } = useParams();

      const stems = kind === 'stems';

      useEffect(() => {
        const getAlbum = async () => {
          await fetchAlbum(uuid, stems);
        };

        getAlbum();
      }, [stems, fetchAlbum]);

      useEffect(() => {
        return () => {
          resetAlbum();
        };
      }, [resetAlbum]);

      const cover = get(album, 'cover.url');
      const tracks = get(album, 'tracks', []);

      const [fileOptions, setFileOptions] = useState([]);

      useEffect(() => {
        if (stems) {
          setFileOptions(stemsOptions);
        } else if (tracks.length) {
          const optionsArr = [];
          tracks.forEach(track => {
            tracksOptions.forEach(({ label, value }) => {
              if (
                value === 'serial' &&
                track.file_wav_48khz &&
                track.file_wav_48khz.url
              ) {
                optionsArr.push({ label, value });
              }
              if (track[value] && track[value].url) {
                optionsArr.push({ label, value });
              }
            });
          });

          setFileOptions(uniqBy(optionsArr, 'value'));
        }
      }, [tracks, stems]);

      return (
        <Layout>
          <Box fullwidth padding={15}>
            {isLoading ? <Loader cover /> : null}
            <Formik
              enableReinitialize
              initialValues={{ ids: [], selectAll: false }}
            >
              {({
                values: { ids },
                isSubmitting,
                resetForm,
                setFieldValue,
              }) => {
                return (
                  <Row withScroll>
                    <Col>
                      <img src={cover} alt={album.name} />
                      {ids.length ? (
                        <>
                          <DownloadBar>
                            {ids.length} SELECTED TRACKS
                          </DownloadBar>
                          <Buttons>
                            <TracksDownloadWrapper>
                              <Label>Download tracks</Label>
                              <Select
                                id="download-album"
                                options={fileOptions}
                                placeholder="Select format"
                                styles={selectStyles}
                                isSearchable={false}
                                onChange={option => downloadTracks(ids, option)}
                                value=""
                              />
                            </TracksDownloadWrapper>
                            <Button
                              type="button"
                              text="CLEAR SELECTED"
                              disabled={isSubmitting}
                              size={SIZE.LARGE}
                              variant={VARIANT.SECONDARY}
                              onClick={resetForm}
                            />
                          </Buttons>
                        </>
                      ) : null}
                    </Col>
                    <Col>
                      <Row separated top>
                        <Col>
                          <Title>{album.name}</Title>
                          <Detail>
                            Directory: <b>{album.directory_name}</b>
                          </Detail>
                          <Detail>
                            Release date: <b>{album.release_date}</b>
                          </Detail>
                        </Col>
                        <AlbumDownloadWrapper>
                          <Label>Download album</Label>
                          <Select
                            id="download-album"
                            options={
                              stems
                                ? stemAlbumOptions
                                : albumOptions(fileOptions)
                            }
                            placeholder="Select format"
                            styles={selectStyles}
                            isSearchable={false}
                            onChange={option => downloadAlbum(album, option)}
                            value=""
                          />
                        </AlbumDownloadWrapper>
                      </Row>
                      <Row />
                      <Row column>
                        {album.labels && album.labels.length > 0
                          ? album.labels.map(l => (
                              <Description key={l.id}>
                                {l.description}
                              </Description>
                            ))
                          : null}
                      </Row>
                      {tracks.length ? (
                        <Row column dimmed style={{ marginTop: '30px' }}>
                          <ListHeader
                            hideAlbumName
                            setFieldValue={setFieldValue}
                            tracks={tracks}
                          />

                          <ScrollableList as={Form}>
                            <TracksList
                              items={tracks}
                              hideAlbumName
                              selectedIds={ids}
                              setFieldValue={setFieldValue}
                              stems={stems}
                            />
                          </ScrollableList>
                        </Row>
                      ) : null}
                    </Col>
                  </Row>
                );
              }}
            </Formik>
          </Box>
        </Layout>
      );
    },
  ),
);

export default AlbumPage;
